import React, {useEffect, useState} from 'react';
import {
  getCurrencies,
  type CurrencyOption,
  updateCompanyGroupAdmin,
  uploadCompanyGroupBrandingAsset,
} from '../core/_requests';
import {CompanyAdmin} from '../core/_models';
import {toast} from 'react-toastify';
import {KTSVG} from '../../../../_metronic/helpers';
import CurrencySelect from './CurrencySelect';
import {getApiErrorMessage, showErrorToast} from '../../../shared/utils/apiErrorMessage';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCompanyEdited: () => void;
  companyAdminData?: CompanyAdmin | null;
}

const EditCompanyAdminModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCompanyEdited,
  companyAdminData,
}) => {
  const [companyName, setCompanyName] = useState('');
  const [contact, setContact] = useState('');
  const [altContact, setAltContact] = useState('');
  const [email, setEmail] = useState('');
  const [addr1, setAddr1] = useState('');
  const [addr2, setAddr2] = useState('');
  const [landmark, setLandmark] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [companyDetails, setCompanyDetails] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [primaryCurrencyCode, setPrimaryCurrencyCode] = useState('INR');
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [watermarkFile, setWatermarkFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [watermarkUrl, setWatermarkUrl] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && companyAdminData) {
      setCompanyName(companyAdminData.name || '');
      setContact(companyAdminData.contactNo || '');
      setAltContact(companyAdminData.altContactNo || '');
      setEmail(companyAdminData.email || '');
      setAddr1(companyAdminData.addressLine1 || '');
      setAddr2(companyAdminData.addressLine2 || '');
      setLandmark(companyAdminData.landmark || '');
      setCountry(companyAdminData.country || '');
      setState(companyAdminData.state || '');
      setCity(companyAdminData.city || '');
      setCompanyDetails(companyAdminData.companyGroupAdminDetails || companyAdminData.companyDetails || '');
      setGstNumber(companyAdminData.gstNumber || '');
      setPanNumber(companyAdminData.panNumber || '');
      setVatNumber(companyAdminData.vatNumber || '');
      setPrimaryCurrencyCode(companyAdminData.primaryCurrencyCode || 'INR');
      setLogoFile(null);
      setWatermarkFile(null);
      setLogoUrl(companyAdminData.logoUrl || '');
      setWatermarkUrl(companyAdminData.watermarkUrl || '');
      setFieldErrors({});
      setError(null);
    }
  }, [isOpen, companyAdminData]);

  useEffect(() => {
    void (async () => {
      try {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        setCurrencies(await getCurrencies());
      } catch (loadError: any) {
        console.error('Failed to load currencies:', loadError);
        setCurrencies([]);
        setCurrenciesError(getApiErrorMessage(loadError, 'Unable to load active currencies.', 'load'));
      } finally {
        setCurrenciesLoading(false);
      }
    })();
  }, []);

  const isImageFile = (file: File) => Boolean(file.type) && file.type.toLowerCase().startsWith('image/');

  const validateBrandingFile = (file: File | null, label: string) => {
    if (!file) return true;
    if (!isImageFile(file)) {
      toast.error(`${label} must be an image file.`, {position: 'top-center'});
      return false;
    }
    return true;
  };

  const validateFields = () => {
    const errors: Record<string, string> = {};

    if (!companyName.trim()) errors.companyName = 'Company Name is required.';
    if (!contact.trim()) {
      errors.contact = 'Contact Number is required.';
    } else if (!/^\+?[0-9\s\-()]{7,20}$/.test(contact)) {
      errors.contact = 'Enter a valid international phone number.';
    }

    if (altContact && !/^\+?[0-9\s\-()]{7,20}$/.test(altContact)) {
      errors.altContact = 'Enter a valid international phone number.';
    }

    if (!email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(email)) {
      errors.email = 'Invalid email format.';
    }

    if (!addr1.trim()) errors.addr1 = 'Address Line 1 is required.';
    if (!country.trim()) errors.country = 'Country is required.';
    if (!state.trim()) errors.state = 'State is required.';

    if (gstNumber.trim()) {
      const cleanGst = gstNumber.trim().toUpperCase();
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(cleanGst)) {
        errors.gstNumber = 'Invalid GSTIN format (e.g. 22AAAAA0000A1Z5).';
      }
    }

    if (panNumber.trim()) {
      const cleanPan = panNumber.trim().toUpperCase();
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(cleanPan)) {
        errors.panNumber = 'Invalid PAN format (e.g. ABCDE1234F).';
      }
    }

    if (vatNumber.trim()) {
      const cleanVat = vatNumber.trim().toUpperCase();
      if (!/^[A-Z0-9]{8,15}$/.test(cleanVat)) {
        errors.vatNumber = 'Invalid VAT format (8-15 alphanumeric characters).';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!validateBrandingFile(file, 'Company logo')) {
      event.target.value = '';
      setLogoFile(null);
      return;
    }
    setLogoFile(file);
  };

  const handleWatermarkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!validateBrandingFile(file, 'Company watermark')) {
      event.target.value = '';
      setWatermarkFile(null);
      return;
    }
    setWatermarkFile(file);
  };

  const handleEditCompanyAdmin = async () => {
    if (!companyAdminData?.id) return;
    if (!validateFields()) return;
    if (
      !validateBrandingFile(logoFile, 'Company logo') ||
      !validateBrandingFile(watermarkFile, 'Company watermark')
    ) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateCompanyGroupAdmin(
        companyAdminData.id,
        companyName,
        contact,
        altContact,
        email,
        addr1,
        addr2,
        landmark,
        country,
        state,
        city,
        companyDetails,
        primaryCurrencyCode,
        true,
        gstNumber,
        panNumber,
        vatNumber
      );

      const uploadWarnings: string[] = [];
      if (logoFile) {
        try {
          const updated = await uploadCompanyGroupBrandingAsset(companyAdminData.id, 'logo', logoFile);
          setLogoUrl(updated?.logoUrl || '');
        } catch (uploadError: any) {
          console.error('Failed to upload company logo:', uploadError);
          uploadWarnings.push(uploadError?.message || 'Logo upload failed.');
        }
      }

      if (watermarkFile) {
        try {
          const updated = await uploadCompanyGroupBrandingAsset(
            companyAdminData.id,
            'watermark',
            watermarkFile
          );
          setWatermarkUrl(updated?.watermarkUrl || '');
        } catch (uploadError: any) {
          console.error('Failed to upload company watermark:', uploadError);
          uploadWarnings.push(uploadError?.message || 'Watermark upload failed.');
        }
      }

      if (uploadWarnings.length > 0) {
        toast.warning(`Company updated, but branding upload issues: ${uploadWarnings.join(' ')}`, {
          position: 'top-center',
        });
      } else {
        toast.success('Company updated successfully');
      }

      onCompanyEdited();
      onClose();
    } catch (err) {
      console.error('Failed to update company:', err);
      const message = getApiErrorMessage(err, 'Failed to update company.', 'update');
      setError(message);
      toast.error(message, {position: 'top-center'});
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className='modal-overlay'>
      <div className='modal-content' onClick={(e) => e.stopPropagation()} style={{maxWidth: '60rem'}}>
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>Edit Company</h5>
          <button className='close-btn' onClick={onClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className='custom-modal-body'>
          {error && <div className='alert alert-danger'>{error}</div>}

          <div className='row'>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Primary Currency <span className='text-danger'>*</span>
              </label>
              <CurrencySelect
                ariaLabel='Primary Currency'
                currencies={currencies}
                loading={currenciesLoading}
                error={currenciesError}
                value={primaryCurrencyCode}
                onChange={setPrimaryCurrencyCode}
                required
              />
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Company Name <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
              {fieldErrors.companyName && <small className='text-danger'>{fieldErrors.companyName}</small>}
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Contact No. <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={contact}
                onChange={(e) => setContact(e.target.value)}
              />
              {fieldErrors.contact && <small className='text-danger'>{fieldErrors.contact}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Alternative Contact No.</label>
              <input
                type='text'
                className='form-control'
                value={altContact}
                onChange={(e) => setAltContact(e.target.value)}
              />
              {fieldErrors.altContact && <small className='text-danger'>{fieldErrors.altContact}</small>}
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Email <span className='text-danger'>*</span>
              </label>
              <input
                type='email'
                className='form-control'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {fieldErrors.email && <small className='text-danger'>{fieldErrors.email}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Address Line 1 <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={addr1}
                onChange={(e) => setAddr1(e.target.value)}
              />
              {fieldErrors.addr1 && <small className='text-danger'>{fieldErrors.addr1}</small>}
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Address Line 2</label>
              <input
                type='text'
                className='form-control'
                value={addr2}
                onChange={(e) => setAddr2(e.target.value)}
              />
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Landmark</label>
              <input
                type='text'
                className='form-control'
                value={landmark}
                onChange={(e) => setLandmark(e.target.value)}
              />
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Country <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
              {fieldErrors.country && <small className='text-danger'>{fieldErrors.country}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                State <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
              {fieldErrors.state && <small className='text-danger'>{fieldErrors.state}</small>}
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>City</label>
              <input
                type='text'
                className='form-control'
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Company Details</label>
              <input
                type='text'
                className='form-control'
                value={companyDetails}
                onChange={(e) => setCompanyDetails(e.target.value)}
              />
            </div>

            <div className='col-md-4 mb-3'>
              <label className='modal_label'>GST Number (15 chars)</label>
              <input
                type='text'
                maxLength={15}
                className='form-control text-uppercase'
                placeholder='e.g. 22AAAAA0000A1Z5'
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value.toUpperCase().slice(0, 15))}
              />
              {fieldErrors.gstNumber && <small className='text-danger'>{fieldErrors.gstNumber}</small>}
            </div>

            <div className='col-md-4 mb-3'>
              <label className='modal_label'>PAN Number (10 chars)</label>
              <input
                type='text'
                maxLength={10}
                className='form-control text-uppercase'
                placeholder='e.g. ABCDE1234F'
                value={panNumber}
                onChange={(e) => setPanNumber(e.target.value.toUpperCase().slice(0, 10))}
              />
              {fieldErrors.panNumber && <small className='text-danger'>{fieldErrors.panNumber}</small>}
            </div>

            <div className='col-md-4 mb-3'>
              <label className='modal_label'>VAT Number (8-15 chars)</label>
              <input
                type='text'
                maxLength={15}
                className='form-control text-uppercase'
                placeholder='e.g. GB123456789'
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value.toUpperCase().slice(0, 15))}
              />
              {fieldErrors.vatNumber && <small className='text-danger'>{fieldErrors.vatNumber}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Company Logo</label>
              <input type='file' accept='image/*' className='form-control' onChange={handleLogoChange} />
              {logoFile && <small className='text-muted'>{logoFile.name}</small>}
              {!logoFile && logoUrl && <small className='text-muted d-block'>Existing: {logoUrl}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Company Watermark</label>
              <input
                type='file'
                accept='image/*'
                className='form-control'
                onChange={handleWatermarkChange}
              />
              {watermarkFile && <small className='text-muted'>{watermarkFile.name}</small>}
              {!watermarkFile && watermarkUrl && (
                <small className='text-muted d-block'>Existing: {watermarkUrl}</small>
              )}
            </div>
          </div>
        </div>

        <div className='d-flex justify-content-end gap-2'>
          <button className='btn btn_secondary' onClick={onClose}>
            Cancel
          </button>
          <button className='btn btn_success' onClick={handleEditCompanyAdmin} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCompanyAdminModal;
